import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { House, Sparkle, Money, Users, CheckCircle, ArrowRight, ArrowLeft, MapPin } from '@phosphor-icons/react';

const LANGUAGES = [
  'English', 'Spanish', 'French', 'German', 'Italian', 
  'Portuguese', 'Chinese', 'Japanese', 'Korean', 'Arabic'
];

const RESPONSE_TIMES = [
  { value: 'within_hour', label: 'Within an hour' },
  { value: 'within_few_hours', label: 'Within a few hours' },
  { value: 'within_day', label: 'Within a day' },
];

// US States list
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming', 'District of Columbia'
];

export default function BecomeHostPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, becomeHost } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    display_name: '',
    host_bio: '',
    host_languages: ['English'],
    host_response_time: 'within_hour',
    state: '',  // US State for affiliate badge tracking
    zip_code: '',  // ZIP code for affiliate badge tracking
  });
  
  // Get step from URL or default to 1
  const step = parseInt(searchParams.get('step')) || 1;
  
  // Function to change step and update URL
  const setStep = (newStep) => {
    const newParams = new URLSearchParams(searchParams);
    if (newStep === 1) {
      newParams.delete('step');
    } else {
      newParams.set('step', newStep.toString());
    }
    // Preserve the ref parameter if it exists
    const ref = searchParams.get('ref');
    if (ref) {
      newParams.set('ref', ref);
    }
    setSearchParams(newParams, { replace: false });
  };
  
  // Capture referral code from URL (e.g., /become-host?ref=ABC123)
  const affiliateRef = searchParams.get('ref');

  // If user is already a host, redirect using useEffect
  useEffect(() => {
    if (user?.is_host) {
      navigate('/host/dashboard');
    }
  }, [user, navigate]);

  // If not logged in, show login prompt instead of redirecting
  if (!user) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-3xl font-serif text-white mb-4">Become a Host</h1>
          <p className="text-zinc-400 mb-8">Please sign in to start hosting on 100Nook</p>
          <Button
            onClick={() => navigate('/auth')}
            className="bg-[#D4AF37] text-black hover:bg-[#C4A030]"
          >
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  // If already a host, show loading while redirecting
  if (user?.is_host) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-white">Redirecting to Host Dashboard...</div>
      </div>
    );
  }

  const handleLanguageToggle = (language) => {
    if (formData.host_languages.includes(language)) {
      if (formData.host_languages.length > 1) {
        setFormData(prev => ({
          ...prev,
          host_languages: prev.host_languages.filter(l => l !== language)
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        host_languages: [...prev.host_languages, language]
      }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.host_bio.trim()) {
      toast.error('Please write a bio about yourself');
      return;
    }

    setLoading(true);
    try {
      // Include affiliate referral code if present in URL
      const submitData = {
        ...formData,
        affiliate_ref: affiliateRef || undefined
      };
      await becomeHost(submitData);
      toast.success('Welcome to hosting! Your profile is now active.');
      navigate('/host/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete host onboarding');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] py-12">
      <div className="max-w-4xl mx-auto px-6">
        
        {/* Step 1: Welcome */}
        {step === 1 && (
          <div className="text-center">
            <img 
              src="/images/logo-full-transparent.png" 
              alt="100Nook" 
              className="h-20 w-auto mx-auto mb-8"
            />
            
            <h1 className="text-4xl md:text-5xl font-serif text-white mb-6">
              Become a Host
            </h1>
            
            <p className="text-xl text-zinc-400 mb-12 max-w-2xl mx-auto">
              Share your private spaces with guests and earn money doing what you love. 
              It only takes a few minutes to get started.
            </p>

            {/* Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="bg-[#121212] border border-white/10 p-8 text-left">
                <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
                  <Money size={24} weight="duotone" className="text-green-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Earn Extra Income</h3>
                <p className="text-zinc-500 text-sm">
                  Set your own prices and availability. Earn money sharing your unique spaces.
                </p>
              </div>

              <div className="bg-[#121212] border border-white/10 p-8 text-left">
                <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center mb-4">
                  <Users size={24} weight="duotone" className="text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Meet New People</h3>
                <p className="text-zinc-500 text-sm">
                  Connect with guests from all walks of life who appreciate unique spaces.
                </p>
              </div>

              <div className="bg-[#121212] border border-white/10 p-8 text-left">
                <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center mb-4">
                  <Sparkle size={24} weight="duotone" className="text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Full Control</h3>
                <p className="text-zinc-500 text-sm">
                  You decide who books, when they can visit, and what rules apply.
                </p>
              </div>
            </div>

            <Button
              onClick={() => setStep(2)}
              data-testid="start-hosting-btn"
              className="bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold px-12 py-4 text-lg"
            >
              Get Started
              <ArrowRight size={20} className="ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Create Profile */}
        {step === 2 && (
          <div>
            <button
              onClick={() => setStep(1)}
              className="flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft size={20} />
              Back
            </button>

            <div className="max-w-2xl mx-auto">
              <h1 className="text-3xl font-serif text-white mb-2">Create Your Host Profile</h1>
              <p className="text-zinc-400 mb-8">Tell guests a bit about yourself</p>

              <div className="space-y-8">
                {/* Display Name */}
                <div>
                  <Label htmlFor="display_name" className="text-white mb-2 block">
                    Display Name <span className="text-zinc-500 text-sm font-normal">(Optional)</span>
                  </Label>
                  <Input
                    id="display_name"
                    data-testid="host-display-name-input"
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    placeholder="A nickname or alias shown to guests (e.g., 'Sarah M.' or 'The Cozy Host')"
                    className="bg-transparent border-white/20 text-white"
                  />
                  <p className="text-zinc-500 text-sm mt-2">
                    This name will be displayed on your listings instead of your real name. Leave blank to use your account name.
                  </p>
                </div>

                {/* Bio */}
                <div>
                  <Label htmlFor="bio" className="text-white mb-2 block">
                    About You <span className="text-[#D4AF37]">*</span>
                  </Label>
                  <Textarea
                    id="bio"
                    data-testid="host-bio-input"
                    value={formData.host_bio}
                    onChange={(e) => setFormData({ ...formData, host_bio: e.target.value })}
                    placeholder="Share a little about yourself - your interests, why you're hosting, and what makes your space special..."
                    className="bg-transparent border-white/20 text-white min-h-[150px]"
                    rows={6}
                  />
                  <p className="text-zinc-500 text-sm mt-2">
                    This will be shown on your listing pages
                  </p>
                </div>

                {/* Languages */}
                <div>
                  <Label className="text-white mb-3 block">Languages You Speak</Label>
                  <div className="flex flex-wrap gap-2">
                    {LANGUAGES.map((language) => (
                      <button
                        key={language}
                        type="button"
                        onClick={() => handleLanguageToggle(language)}
                        className={`px-4 py-2 rounded-full border transition-all ${
                          formData.host_languages.includes(language)
                            ? 'bg-[#D4AF37] border-[#D4AF37] text-black'
                            : 'border-white/20 text-zinc-400 hover:border-white/40'
                        }`}
                      >
                        {language}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Response Time */}
                <div>
                  <Label className="text-white mb-3 block">Typical Response Time</Label>
                  <div className="space-y-2">
                    {RESPONSE_TIMES.map((time) => (
                      <label
                        key={time.value}
                        className={`flex items-center p-4 border cursor-pointer transition-all ${
                          formData.host_response_time === time.value
                            ? 'border-[#D4AF37] bg-[#D4AF37]/5'
                            : 'border-white/10 hover:border-white/20'
                        }`}
                      >
                        <input
                          type="radio"
                          name="response_time"
                          value={time.value}
                          checked={formData.host_response_time === time.value}
                          onChange={(e) => setFormData({ ...formData, host_response_time: e.target.value })}
                          className="hidden"
                        />
                        <div className={`w-5 h-5 rounded-full border-2 mr-4 flex items-center justify-center ${
                          formData.host_response_time === time.value
                            ? 'border-[#D4AF37]'
                            : 'border-white/30'
                        }`}>
                          {formData.host_response_time === time.value && (
                            <div className="w-2.5 h-2.5 rounded-full bg-[#D4AF37]" />
                          )}
                        </div>
                        <span className="text-white">{time.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Location (for affiliate tracking) */}
                <div className="bg-[#0A0A0A] border border-white/5 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <MapPin size={20} className="text-[#D4AF37]" />
                    <Label className="text-white">Your Location</Label>
                    <span className="text-xs text-zinc-500">(US Only)</span>
                  </div>
                  <p className="text-zinc-400 text-sm mb-4">
                    This helps us connect you with local guests and is used for affiliate tracking.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="state" className="text-zinc-400 text-sm mb-2 block">
                        State <span className="text-[#D4AF37]">*</span>
                      </Label>
                      <select
                        id="state"
                        data-testid="host-state-select"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        className="w-full bg-transparent border border-white/20 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                      >
                        <option value="">Select State</option>
                        {US_STATES.map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="zip_code" className="text-zinc-400 text-sm mb-2 block">
                        ZIP Code <span className="text-[#D4AF37]">*</span>
                      </Label>
                      <Input
                        id="zip_code"
                        data-testid="host-zip-input"
                        value={formData.zip_code}
                        onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                        placeholder="12345"
                        maxLength={10}
                        className="bg-transparent border-white/20 text-white"
                      />
                    </div>
                  </div>
                </div>

                <p className="text-sm text-zinc-400 text-center mb-4">
                  By clicking Continue, you agree to the{' '}
                  <Link to="/terms" className="text-[#D4AF37] hover:underline">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/host-agreement" className="text-[#D4AF37] hover:underline">Host Agreement</Link>
                  {' '}of 100Nook.
                </p>

                <Button
                  onClick={() => setStep(3)}
                  disabled={!formData.host_bio.trim() || !formData.state || !formData.zip_code}
                  data-testid="continue-to-review-btn"
                  className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold py-4 disabled:opacity-50"
                >
                  Continue
                  <ArrowRight size={20} className="ml-2" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Review & Confirm */}
        {step === 3 && (
          <div>
            <button
              onClick={() => setStep(2)}
              className="flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft size={20} />
              Back
            </button>

            <div className="max-w-2xl mx-auto">
              <h1 className="text-3xl font-serif text-white mb-2">Review Your Profile</h1>
              <p className="text-zinc-400 mb-8">Make sure everything looks good</p>

              <div className="bg-[#121212] border border-white/10 p-8 mb-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl font-serif text-[#D4AF37]">
                      {(formData.display_name || user?.name)?.charAt(0) || 'H'}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">{formData.display_name || user?.name}</h3>
                    {formData.display_name && (
                      <p className="text-zinc-400 text-sm">Displaying as: {formData.display_name}</p>
                    )}
                    <p className="text-zinc-500">Host since {new Date().getFullYear()}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {formData.display_name && (
                    <div>
                      <p className="text-zinc-500 text-sm mb-1">Display Name</p>
                      <p className="text-white">{formData.display_name}</p>
                    </div>
                  )}

                  <div>
                    <p className="text-zinc-500 text-sm mb-1">About</p>
                    <p className="text-white">{formData.host_bio}</p>
                  </div>

                  <div>
                    <p className="text-zinc-500 text-sm mb-1">Languages</p>
                    <p className="text-white">{formData.host_languages.join(', ')}</p>
                  </div>

                  <div>
                    <p className="text-zinc-500 text-sm mb-1">Response time</p>
                    <p className="text-white">
                      {RESPONSE_TIMES.find(t => t.value === formData.host_response_time)?.label}
                    </p>
                  </div>

                  <div>
                    <p className="text-zinc-500 text-sm mb-1">Location</p>
                    <p className="text-white">{formData.state}, {formData.zip_code}</p>
                  </div>
                </div>
              </div>

              {/* What's Next */}
              <div className="bg-[#0A0A0A] border border-white/5 p-6 mb-8">
                <h3 className="text-lg font-semibold text-white mb-4">What happens next?</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle size={20} weight="fill" className="text-green-500 mt-0.5" />
                    <p className="text-zinc-400">Your host profile will be created immediately</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle size={20} weight="fill" className="text-green-500 mt-0.5" />
                    <p className="text-zinc-400">You can create your first listing right away</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle size={20} weight="fill" className="text-green-500 mt-0.5" />
                    <p className="text-zinc-400">Your listing will be reviewed by our team (usually within 24 hours)</p>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleSubmit}
                disabled={loading}
                data-testid="complete-hosting-btn"
                className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold py-4"
              >
                {loading ? 'Creating Profile...' : 'Complete & Start Hosting'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
